import {
  Button,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  useToast,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ArticleTemplate,
  UpdateArticleTemplateRequest,
  articleTemplatesApi,
} from '@texas/api/endpoints/articleTemplatesApi';
import {
  MatrixNode,
  matrixConfigurationApi,
} from '@texas/api/endpoints/matrixConfigurationApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { useActiveSession } from '@texas/hooks/useSession';

const emptyTemplate: UpdateArticleTemplateRequest = {
  name: '',
  matrixNodeXId: null,
  matrixNodeYId: null,
  itemCodeMinLength: null,
  itemCodeMaxLength: null,
  hasItemCodeMinLength: false,
  hasItemCodeMaxLength: false,
  displayOrder: 0,
  archived: false,
};

export function ArticleTemplateModal({
  canEdit,
  isOpen,
  onClose,
  onSaved,
  template,
}: {
  canEdit: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  template: ArticleTemplate | null;
}) {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const toast = useToast();
  const {
    data: nodes,
    refetch: loadNodes,
    error: nodesError,
  } = useApiResource(matrixConfigurationApi.getNodes);
  const {
    request: create,
    loading: creating,
    error: createError,
  } = useApiRequest(articleTemplatesApi.create);
  const {
    request: update,
    loading: updating,
    error: updateError,
  } = useApiRequest(articleTemplatesApi.update);
  const { register, handleSubmit, reset, watch, setValue, control } =
    useForm<UpdateArticleTemplateRequest>({ defaultValues: emptyTemplate });

  useEffect(() => {
    if (isOpen) loadNodes(currentUser.organizationId);
  }, [currentUser.organizationId, isOpen, loadNodes]);

  useEffect(() => {
    reset(
      template
        ? {
            ...template,
            matrixNodeXId: template.matrixNodeXId || null,
            matrixNodeYId: template.matrixNodeYId || null,
            hasItemCodeMinLength: template.itemCodeMinLength !== null,
            hasItemCodeMaxLength: template.itemCodeMaxLength !== null,
          }
        : emptyTemplate,
    );
  }, [isOpen, reset, template]);

  const submit = async (form: UpdateArticleTemplateRequest) => {
    const request: UpdateArticleTemplateRequest = {
      ...form,
      matrixNodeXId: form.matrixNodeXId ?? 0,
      matrixNodeYId: form.matrixNodeYId ?? 0,
      hasItemCodeMinLength: form.itemCodeMinLength !== null,
      hasItemCodeMaxLength: form.itemCodeMaxLength !== null,
    };
    const response = template
      ? await update(template.id, request)
      : await create(currentUser.organizationId, {
          ...request,
          matrixNodeXId: form.matrixNodeXId,
          matrixNodeYId: form.matrixNodeYId,
        });
    if (response.hasError) return;
    toast({
      title:
        template && request.archived !== template.archived
          ? t(
              request.archived
                ? 'general.entityArchived'
                : 'general.entityRestored',
              { name: request.name },
            )
          : template
          ? t('general.updated')
          : t('general.created'),
      status: 'success',
      isClosable: true,
    });
    onSaved();
    onClose();
  };

  const availableNodes = (axis: 'X' | 'Y') =>
    (nodes ?? [])
      .filter((node) => !node.archived && node.allowedAxis === axis)
      .sort(sortNodes);

  return (
    <Modal
      variant="entityDirectory"
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {template ? t('general.edit') : t('general.new')}{' '}
          {t('configuration.articleTemplate').toLowerCase()}
        </ModalHeader>
        <form onSubmit={handleSubmit(submit)}>
          <ModalBody>
            <ErrorDetails error={createError ?? updateError ?? nodesError} />
            <Flex direction="column" gap="4">
              <FormControl isRequired={true}>
                <TexasFormLabel>{t('general.name')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  isDisabled={!canEdit}
                  {...register('name', { required: true, maxLength: 100 })}
                />
                <HookFormCounter
                  control={control}
                  name="name"
                  maxLength={100}
                />
              </FormControl>
              <FormControl>
                <TexasFormLabel>
                  {t('configuration.matrixNodeY')}
                </TexasFormLabel>
                <Select
                  variant="entityDirectory"
                  isDisabled={!canEdit}
                  value={watch('matrixNodeYId') ?? ''}
                  onChange={(event) =>
                    setValue(
                      'matrixNodeYId',
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                >
                  <option value="">{t('general.none')}</option>
                  {availableNodes('Y').map((node) => (
                    <option key={node.id} value={node.id}>
                      {node.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <TexasFormLabel>
                  {t('configuration.matrixNodeX')}
                </TexasFormLabel>
                <Select
                  variant="entityDirectory"
                  isDisabled={!canEdit}
                  value={watch('matrixNodeXId') ?? ''}
                  onChange={(event) =>
                    setValue(
                      'matrixNodeXId',
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                >
                  <option value="">{t('general.none')}</option>
                  {availableNodes('X').map((node) => (
                    <option key={node.id} value={node.id}>
                      {node.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <TexasFormLabel>
                  {t('configuration.itemCodeMinLength')}
                </TexasFormLabel>
                <Input
                  type="number"
                  variant="entityDirectory"
                  isDisabled={!canEdit}
                  {...register('itemCodeMinLength', {
                    setValueAs: (value) =>
                      value === '' ? null : Number(value),
                  })}
                />
              </FormControl>
              <FormControl>
                <TexasFormLabel>
                  {t('configuration.itemCodeMaxLength')}
                </TexasFormLabel>
                <Input
                  type="number"
                  variant="entityDirectory"
                  isDisabled={!canEdit}
                  {...register('itemCodeMaxLength', {
                    setValueAs: (value) =>
                      value === '' ? null : Number(value),
                  })}
                />
              </FormControl>
              <FormControl>
                <TexasFormLabel>{t('branch.displayOrder')}</TexasFormLabel>
                <Input
                  type="number"
                  variant="entityDirectory"
                  isDisabled={!canEdit}
                  {...register('displayOrder', { valueAsNumber: true })}
                />
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter gap="2">
            {template && canEdit && (
              <Button
                variant="ghost"
                onClick={() => {
                  setValue('archived', true);
                  handleSubmit(submit)();
                }}
              >
                {t('general.archive')}
              </Button>
            )}
            {canEdit && (
              <SubmitButton loading={creating || updating}>
                {t('general.save')}
              </SubmitButton>
            )}
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

function sortNodes(left: MatrixNode, right: MatrixNode) {
  return (
    left.displayOrder - right.displayOrder ||
    left.name.localeCompare(right.name)
  );
}
